/* eslint-disable @typescript-eslint/no-explicit-any */
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { useUtils } from "../../customHooks/useUtils";
import { formatCurrency, formatCustomDate } from "../../utils/utils";
import CardError from "../feedBacks/CardError";
import Loader from "../feedBacks/loader";
import NoDeviceCardError from "../feedBacks/NoDeviceCardError";
import { IconWrapper } from "../../customHooks/useIcon";

// Function to determine font size class based on content length
const getFontSizeClass = (formattedValue: string): string => {
  const length = formattedValue.length;

  if (length > 15) return "text-lg";
  if (length > 12) return "text-xl";
  if (length > 9) return "text-2xl";
  return "text-3xl";
};

interface StatsCardProps {
  title: string;
  value: string;
  isError: boolean;
  isLoading: boolean;
  previousMonthValue?: string | number;
  className?: string;
  cardErrorStyle?: string;
  errorType?: string;
  time?: string;
  period?: string;
  currency?: string;
}

function StatsCard({
  title,
  value,
  isLoading,
  isError,
  previousMonthValue,
  className = "",
  cardErrorStyle,
  errorType,
  time,
  period,
  currency,
}: StatsCardProps) {
  const { FEEDBACK_MESSAGES, t } = useUtils();
const formatHoursToDaysAndHours = (hours: number): JSX.Element => {
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = +(hours % 24).toFixed(1);
    return (
      <>
        <span className="text-2xl font-bold">{days}</span>
        <span className="text-sm ml-1 text-primary-blackLight">
          {days > 1 ? t("common.days") : t("common.day")}
        </span>{" "}
        <span className="text-2xl font-bold">
          {parseInt(Math.round(remainingHours).toString())}
        </span>
        <span className="text-sm ml-1 text-primary-blackLight">
          {remainingHours > 1 ? t("common.hours") : t("common.hour")}
        </span>
      </>
    );
  }
  return (
    <>
      <span className="text-2xl font-bold">{parseInt(Math.round(hours).toString())}</span>
      <span className="text-sm ml-1 text-primary-blackLight">
        {hours > 1 ? t("common.hours") : t("common.hour")}
      </span>
    </>
  );
};
  const translatedTitles = {
    revenue: t("readings.total_revenue"),
    expectedRevenue: t("readings.expected_revenue"),
    downtimeLosses: t("readings.downtime_losses"),
    tariffLosses: t("readings.tariff_losses"),
    powerCuts: t("readings.power_cuts"),
    distributedElectricity: t("readings.distributed_electricity"),
    avgAvailability: t("readings.avg_availability"),
    assetType: t("readings.asset_type"),
    revenuePerHour: t("readings.revenue_per_hour"),
  };
  const formatValue = (title: string, value: string): any => {
    if (
      [
        translatedTitles.revenue,
        translatedTitles.downtimeLosses,
        translatedTitles.tariffLosses,
        translatedTitles.expectedRevenue,
      ].some((txt) => title.toLowerCase().includes(txt.toLowerCase()))
    ) {
      return `${formatCurrency(parseInt((value)))}`;
    }

    if (
      [
        // translatedTitles.powerCuts,
        translatedTitles.distributedElectricity,
        // translatedTitles.avgAvailability,
      ].some((txt) => title.toLowerCase().includes(txt.toLowerCase()))
    ) {
      return Number(value).toFixed(1);
    }
    if (
      [
        translatedTitles.powerCuts,
        translatedTitles.avgAvailability,
      ].some((txt) => title.toLowerCase().includes(txt.toLowerCase()))
    ) {
      return formatHoursToDaysAndHours(Number(value));
    }

    if (
      [translatedTitles.assetType, translatedTitles.tariffLosses].some((txt) =>
        title.toLowerCase().includes(txt.toLowerCase())
      )
    ) {
      return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    }

    return value;
  };

  const renderContent = () => {
    if (isLoading) return <Loader />;

    if (isError) {
      return errorType === FEEDBACK_MESSAGES.NO_DEVICE ? (
        <NoDeviceCardError />
      ) : (
        <CardError
          message={FEEDBACK_MESSAGES.ERROR}
          className={cardErrorStyle}
        />
      );
    }

    const numericValue = parseFloat(value);
    const numericPrev = previousMonthValue
      ? parseFloat(previousMonthValue as string)
      : null;
    let percentageChange: number | null = null;
    let isIncrease: boolean | null = null;

    if (numericPrev !== null && !isNaN(numericPrev) && numericPrev !== 0) {
      percentageChange = ((numericValue - numericPrev) / numericPrev) * 100;
      isIncrease = percentageChange > 0;
    }

    const formattedValue = formatValue(title, value);
    const fontSizeClass = getFontSizeClass(formattedValue);
    const mainFormattedValue = formatCurrency(parseFloat(value));
    const isRevenueType = [
      translatedTitles.revenue,
      translatedTitles.downtimeLosses,
      translatedTitles.tariffLosses,
      translatedTitles.expectedRevenue,
      translatedTitles.revenuePerHour
    ].some((txt) => title.toLowerCase().includes(txt.toLowerCase()));
    return (
      <>
        <h3
          className={`${fontSizeClass} font-bold truncate max-w-full px-4 ${
            typeof value !== "string" ? "tracking-wider" : ""
          }`}
        >
          {isRevenueType ? (
            <>
              <span>{mainFormattedValue}</span>
              {currency && (
                <span className="ml-1 text-sm text-gray-500">{currency}</span>
              )}
            </>
          ) : (
            formattedValue
          )}
        </h3>

        {percentageChange !== null && (
          <div className="flex items-center text-sm mb-2">
            {isIncrease ? (
              <IconWrapper
                icon={FaArrowUp}
                className="w-4 h-4 text-green-500 mr-1"
              />
            ) : (
              <IconWrapper
                icon={FaArrowDown}
                className="w-3 h-3 text-red-500 mr-1"
              />
            )}
            <span
              className={`text-[12px] ${
                isIncrease ? "text-green-500" : "text-red-500"
              }`}
            >
              {Math.abs(percentageChange).toFixed(1)}%{" "}
              {isIncrease ? "higher " : "lower "}
            </span>
          </div>
        )}

        <p className="text-sm text-center font-light max-w-[90%] px-2">
          {title}
        </p>
        {time && (
          <p className="text-xs mt-1 text-center text-primary-yellowMain">
            {formatCustomDate(time)}
          </p>
        )}
        {period && (
          <p className="text-xs mt-1 text-center text-primary-yellowMain">
            {period}
          </p>
        )}
      </>
    );
  };

  return (
    <div
      className={`
        bg-white flex flex-col w-full h-[130px] 
        rounded-2xl  items-center justify-center 
        border border-primary-border text-primary-blackMain 
        ${className}
      `}
    >
      {renderContent()}
    </div>
  );
}

export default StatsCard;
